/**
 * EditMDOrderDialog - Edit MD Order Dialog Component
 * Extracted from ClientDetailPage
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DischargeOrderFields } from "./DischargeOrderFields";
import { ServiceHoldFields } from "./ServiceHoldFields";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ORDER_TYPES = [
  { value: 'personal_care', label: 'Personal Care Service Evaluation and Treatment and Last 3 MD visit notes' },
  { value: 'dme_supplies', label: 'DME Supplies' },
  { value: 'service_hold', label: 'Service Hold' },
  { value: 'discharge', label: 'Discharge' }
];

export function EditMDOrderDialog({
  open,
  onOpenChange,
  editingMdOrder,
  setEditingMdOrder,
  onSave,
}) {
  if (!editingMdOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit MD Order</DialogTitle>
          <DialogDescription>
            Modify the MD Order details before signing
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Order Types */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Order</Label>
            <div className="grid grid-cols-1 gap-3">
              {ORDER_TYPES.map((type) => (
                <div key={type.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-md-${type.value}`}
                    checked={editingMdOrder.order_types?.includes(type.value)}
                    onCheckedChange={(checked) => {
                      setEditingMdOrder(prev => ({
                        ...prev,
                        order_types: checked
                          ? [...(prev.order_types || []), type.value]
                          : (prev.order_types || []).filter(t => t !== type.value)
                      }));
                    }}
                  />
                  <Label htmlFor={`edit-md-${type.value}`} className="text-sm">
                    {type.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Physician Info */}
          <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-800">Physician Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Physician Name</Label>
                <Input
                  value={editingMdOrder.physician_name || ""}
                  onChange={(e) => setEditingMdOrder(prev => ({ ...prev, physician_name: e.target.value }))}
                  placeholder="Dr. John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Physician Phone</Label>
                <Input
                  value={editingMdOrder.physician_phone || ""}
                  onChange={(e) => setEditingMdOrder(prev => ({ ...prev, physician_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Physician Fax</Label>
                <Input
                  value={editingMdOrder.physician_fax || ""}
                  onChange={(e) => setEditingMdOrder(prev => ({ ...prev, physician_fax: e.target.value }))}
                  placeholder="(555) 123-4568"
                />
              </div>
              <div className="space-y-2">
                <Label>NPI Number</Label>
                <Input
                  value={editingMdOrder.physician_npi || ""}
                  onChange={(e) => setEditingMdOrder(prev => ({ ...prev, physician_npi: e.target.value }))}
                  placeholder="1234567890"
                />
              </div>
            </div>
          </div>

          {/* Patient Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Patient Height</Label>
              <Input
                value={editingMdOrder.patient_height || ""}
                onChange={(e) => setEditingMdOrder(prev => ({ ...prev, patient_height: e.target.value }))}
                placeholder="5'8&quot;"
              />
            </div>
            <div className="space-y-2">
              <Label>Patient Weight</Label>
              <Input
                value={editingMdOrder.patient_weight || ""}
                onChange={(e) => setEditingMdOrder(prev => ({ ...prev, patient_weight: e.target.value }))}
                placeholder="165 lbs"
              />
            </div>
          </div>

          {/* Personal Care Section */}
          {editingMdOrder.order_types?.includes('personal_care') && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800">Personal Care Service Evaluation and Treatment</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Last 3 MD Office Visit Notes <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={editingMdOrder.md_visit_notes || ""}
                    onChange={(e) => setEditingMdOrder(prev => ({ ...prev, md_visit_notes: e.target.value }))}
                    placeholder="Please provide the last 3 MD office visit notes for this patient..."
                    rows={4}
                  />
                  <p className="text-xs text-blue-600">Required: Upload or document the patient&apos;s last 3 physician office visit notes</p>
                </div>
              </div>
            </div>
          )}

          {/* DME Section */}
          {editingMdOrder.order_types?.includes('dme_supplies') && (
            <div className="space-y-3 p-4 bg-emerald-50 rounded-lg">
              <h4 className="text-sm font-medium text-emerald-800">DME/Supplies Details</h4>
              <div className="space-y-2">
                <Label>DME Items/Supplies</Label>
                <Textarea
                  value={editingMdOrder.dme_other || ""}
                  onChange={(e) => setEditingMdOrder(prev => ({ ...prev, dme_other: e.target.value }))}
                  placeholder="List DME items and supplies needed..."
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Service Hold Section */}
          {editingMdOrder.order_types?.includes('service_hold') && (
            <ServiceHoldFields
              idPrefix="edit-hold"
              value={editingMdOrder}
              onChange={(patch) => setEditingMdOrder(prev => ({ ...prev, ...patch }))}
            />
          )}

          {/* Discharge Section */}
          {editingMdOrder.order_types?.includes('discharge') && (
            <DischargeOrderFields
              idPrefix="edit-discharge"
              value={editingMdOrder}
              onChange={(patch) => setEditingMdOrder(prev => ({ ...prev, ...patch }))}
            />
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={editingMdOrder.notes || ""}
              onChange={(e) => setEditingMdOrder(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes or instructions..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setEditingMdOrder(null); }}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
